//-------------------------------------------------
// step:2 Curriculum Builder (Prepare Session)
//-------------------------------------------------
// Load LMS topics for the draft track/subjects; filter by subject + search.
// Footer actions live on Active Learning Path ("Add to Timeline").
//-------------------------------------------------

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppLayout, PageMain } from '../components/WizardShell';
import { TopicLibrary } from '../components/trainer/TopicLibrary';
import { ActiveLearningPath } from '../components/trainer/ActiveLearningPath';
import { WorkshopDayLearningPath } from '../components/trainer/WorkshopDayLearningPath';
import { ROUTES } from '../utils/createRoutes';
import type { Topic, TopicSubtopic } from '../types/curriculumTopic';
import {
  isAdminCreateSessionFlow,
  loadCreateSessionDraft,
  saveCreateSessionDraft,
} from '../utils/createSessionFlowStorage';
import {
  allTopicsHaveDuration,
  loadCurriculumPath,
  plannerTopicsToCurriculumTopics,
  saveCurriculumPathForTimeline,
} from '../utils/curriculumPathStorage';
import { loadPlannerTopics } from '../utils/topicPlannerStorage';
import { isWorkshopCategory } from '../utils/sessionCategoryUtils';
import {
  groupTopicsByWorkshopDay,
  mergeDayTimings,
  type WorkshopDayTiming,
} from '../utils/workshopSessionUtils';
import {
  fetchSubjectsForTrack,
  fetchTopicsForSubject,
  getTopicDescription,
  type ContentSubject,
  type ContentTopic,
} from '../../../utils/contentUtils';
import { useLearningPathResize } from '../hooks/useLearningPathResize';

type LibraryTopic = Topic & { subjectId: string; subjectName: string };

function toLibraryTopic(topic: ContentTopic, subject: ContentSubject): LibraryTopic {
  const title = String(topic.topic_name || '').trim();
  return {
    id: String(topic.topic_id || `${subject.subject_id}-${title}`),
    title,
    description: getTopicDescription(topic),
    durationMinutes: null,
    category: 'fundamentals',
    subjectId: subject.subject_id,
    subjectName: subject.subject_name,
  };
}

/** Prefer saved curriculum path; fall back to current planner topics + times. */
function loadSelectedTopicsFromSession(): Topic[] {
  const fromPath = loadCurriculumPath();
  if (fromPath.length > 0) return fromPath;
  return plannerTopicsToCurriculumTopics(loadPlannerTopics());
}

/**
 * Map session-creation day topics onto library rows (real topic ids) so Prepare
 * shows them as already selected on the path (hidden from the left library).
 */
function hydrateTopicsFromWorkshopDraft(
  libraryTopics: LibraryTopic[],
  dayTopics: string[][],
  existing: Topic[],
): Topic[] {
  const byTitle = new Map(
    libraryTopics.map((t) => [t.title.trim().toLowerCase(), t]),
  );
  const existingByTitle = new Map(
    existing.map((t) => [t.title.trim().toLowerCase(), t]),
  );
  const result: Topic[] = [];
  const seen = new Set<string>();

  dayTopics.forEach((day, dayIndex) => {
    for (const raw of day) {
      const title = String(raw || '').trim();
      if (!title) continue;
      const key = title.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const lib = byTitle.get(key);
      const prev = existingByTitle.get(key);
      if (lib) {
        result.push({
          id: lib.id,
          title: lib.title,
          description: lib.description || prev?.description || '',
          durationMinutes: prev?.durationMinutes ?? null,
          category: 'fundamentals',
          workshopDayIndex: dayIndex,
          subtopics: prev?.subtopics,
        });
      } else {
        result.push({
          id: prev?.id || `draft-topic-${dayIndex + 1}-${key}`,
          title,
          description: prev?.description || `Workshop Day ${dayIndex + 1}`,
          durationMinutes: prev?.durationMinutes ?? null,
          category: 'fundamentals',
          workshopDayIndex: dayIndex,
          subtopics: prev?.subtopics,
        });
      }
    }
  });
  return result;
}

/** Keep existing durations/ids; append any draft titles still missing. */
function mergeDraftTopicsIntoSelection(
  current: Topic[],
  libraryTopics: LibraryTopic[],
  dayTopics: string[][],
): Topic[] {
  if (!dayTopics.length) return current;
  const hydrated = hydrateTopicsFromWorkshopDraft(
    libraryTopics,
    dayTopics,
    current,
  );
  if (current.length === 0) return hydrated;

  const byTitle = new Map(current.map((t) => [t.title.trim().toLowerCase(), t]));
  const merged: Topic[] = [];
  const used = new Set<string>();

  for (const h of hydrated) {
    const key = h.title.trim().toLowerCase();
    const existing = byTitle.get(key);
    used.add(key);
    if (existing) {
      merged.push({
        ...existing,
        id: h.id.startsWith('draft-') ? existing.id : h.id,
        workshopDayIndex: h.workshopDayIndex,
        durationMinutes: existing.durationMinutes ?? h.durationMinutes,
        subtopics: existing.subtopics ?? h.subtopics,
      });
    } else {
      merged.push(h);
    }
  }
  // Keep any extras the trainer added that aren't in the original draft
  for (const t of current) {
    const key = t.title.trim().toLowerCase();
    if (!used.has(key)) merged.push(t);
  }
  return merged;
}

export function CurriculumBuilder() {
  const navigate = useNavigate();
  const location = useLocation();
  const adminCreateFlow = isAdminCreateSessionFlow();
  const createDraft = adminCreateFlow ? loadCreateSessionDraft() : null;
  const draftTrackId = createDraft?.trackId || '';
  const draftSubjectsKey = (createDraft?.subjects || []).join('|');

  const [contentSubjects, setContentSubjects] = useState<ContentSubject[]>([]);
  const [libraryTopics, setLibraryTopics] = useState<LibraryTopic[]>([]);
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>(() =>
    loadSelectedTopicsFromSession(),
  );
  const isWorkshop = isWorkshopCategory(createDraft?.sessionCategory);
  const workshopDayCount = Math.max(1, createDraft?.workshopDays || 1);
  const [daySchedules, setDaySchedules] = useState<WorkshopDayTiming[]>(() =>
    mergeDayTimings(
      workshopDayCount,
      createDraft?.startDate || '',
      createDraft?.startTime || '',
      createDraft?.daySchedules,
    ),
  );
  const [addDayTarget, setAddDayTarget] = useState(0);
  const [draftHydrated, setDraftHydrated] = useState(false);

  // Auto-select workshop topics from session creation once the library loads.
  // Skip when curriculum is locked from the course live session plan.
  useEffect(() => {
    if (draftHydrated) return;
    if (createDraft?.fromCoursePlan) {
      setDraftHydrated(true);
      return;
    }
    if (!isWorkshop || !createDraft?.dayTopics?.length) {
      if (!isWorkshop) setDraftHydrated(true);
      return;
    }
    if (loadingTopics) return;

    const continueFromPlanner = Boolean(
      (location.state as { continueFromPlanner?: boolean } | null)
        ?.continueFromPlanner,
    );

    setSelectedTopics((prev) => {
      if (continueFromPlanner && prev.length > 0) {
        return mergeDraftTopicsIntoSelection(
          prev,
          libraryTopics,
          createDraft.dayTopics,
        );
      }
      return mergeDraftTopicsIntoSelection(
        prev,
        libraryTopics,
        createDraft.dayTopics,
      );
    });
    setDraftHydrated(true);
  }, [
    draftHydrated,
    isWorkshop,
    createDraft?.dayTopics,
    createDraft?.fromCoursePlan,
    libraryTopics,
    loadingTopics,
    location.state,
  ]);

  // Tag seeded topics with workshop day when draft has dayTopics but topics lack index.
  useEffect(() => {
    if (!isWorkshop || !createDraft?.dayTopics?.length) return;
    setSelectedTopics((prev) => {
      if (prev.length === 0) return prev;
      const titleToDay = new Map<string, number>();
      createDraft.dayTopics.forEach((day, dayIndex) => {
        for (const title of day) titleToDay.set(title.trim().toLowerCase(), dayIndex);
      });
      let changed = false;
      const next = prev.map((t) => {
        if (typeof t.workshopDayIndex === 'number') return t;
        const day = titleToDay.get(t.title.trim().toLowerCase());
        if (day === undefined) return t;
        changed = true;
        return { ...t, workshopDayIndex: day };
      });
      return changed ? next : prev;
    });
  }, [isWorkshop, createDraft?.dayTopics]);

  // Keep path in sync so Add Topic ↔ Planner round-trips never lose times.
  useEffect(() => {
    if (selectedTopics.length === 0) return;
    saveCurriculumPathForTimeline(selectedTopics);
  }, [selectedTopics]);

  // Re-hydrate when navigating back from Planner via Add Topic / Back.
  useEffect(() => {
    const continueFromPlanner = Boolean(
      (location.state as { continueFromPlanner?: boolean } | null)
        ?.continueFromPlanner,
    );
    if (!continueFromPlanner) return;
    setSelectedTopics(loadSelectedTopicsFromSession());
  }, [location.key, location.state]);

  //-------------------------------------------------
  // step:1 load subjects for draft track, then topics
  //-------------------------------------------------
  useEffect(() => {
    if (!draftTrackId) {
      setContentSubjects([]);
      setLibraryTopics([]);
      return;
    }

    const draftSubjectNames = draftSubjectsKey
      ? draftSubjectsKey.split('|').map((s) => s.toLowerCase())
      : [];

    let cancelled = false;
    (async () => {
      setLoadingTopics(true);
      setLoadError(null);
      try {
        const subjects = await fetchSubjectsForTrack(draftTrackId, {
          excludeProject: true,
        });
        if (cancelled) return;

        const scoped =
          draftSubjectNames.length > 0
            ? subjects.filter((s) =>
                draftSubjectNames.includes(s.subject_name.toLowerCase()),
              )
            : subjects;

        setContentSubjects(scoped);
        if (scoped.length === 1) {
          setSubjectFilter(scoped[0].subject_id);
        } else {
          setSubjectFilter('all');
        }

        const topicLists = await Promise.all(
          scoped.map(async (subject) => {
            const rows = await fetchTopicsForSubject(subject.subject_id);
            return rows
              .filter((t) => !t.del_row && String(t.topic_name || '').trim())
              .map((t) => toLibraryTopic(t, subject));
          }),
        );
        if (cancelled) return;
        setLibraryTopics(topicLists.flat());
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setLoadError('Failed to load subjects/topics for this track.');
          setContentSubjects([]);
          setLibraryTopics([]);
        }
      } finally {
        if (!cancelled) setLoadingTopics(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [draftTrackId, draftSubjectsKey]);

  const subjectFilters = useMemo(() => {
    const chips = contentSubjects.map((s) => ({
      id: s.subject_id,
      label: s.subject_name,
    }));
    if (chips.length <= 1) return chips;
    return [{ id: 'all', label: 'All subjects' }, ...chips];
  }, [contentSubjects]);

  const selectedTitleSet = useMemo(
    () => new Set(selectedTopics.map((t) => t.title.trim().toLowerCase())),
    [selectedTopics],
  );

  /** IDs of selected topics PLUS any library topics whose title matches a selected one. */
  const selectedIds = useMemo(() => {
    const ids = new Set(selectedTopics.map((t) => t.id));
    for (const lt of libraryTopics) {
      if (selectedTitleSet.has(lt.title.trim().toLowerCase())) ids.add(lt.id);
    }
    return ids;
  }, [selectedTopics, libraryTopics, selectedTitleSet]);

  const selectedSubIds = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    const titleToSubs = new Map<string, Set<string>>();
    for (const t of selectedTopics) {
      if (t.subtopics?.length) {
        const subIds = new Set(t.subtopics.filter((s) => s.selected).map((s) => s.id));
        map[t.id] = subIds;
        titleToSubs.set(t.title.trim().toLowerCase(), subIds);
      }
    }
    for (const lt of libraryTopics) {
      if (!map[lt.id]) {
        const subs = titleToSubs.get(lt.title.trim().toLowerCase());
        if (subs) map[lt.id] = subs;
      }
    }
    return map;
  }, [selectedTopics, libraryTopics]);

  const filteredTopics = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return libraryTopics.filter((topic) => {
      const matchesSubject =
        subjectFilter === 'all' || topic.subjectId === subjectFilter;
      const matchesSearch =
        !q ||
        topic.title.toLowerCase().includes(q) ||
        topic.description.toLowerCase().includes(q) ||
        topic.subjectName.toLowerCase().includes(q);
      return matchesSubject && matchesSearch;
    });
  }, [libraryTopics, searchQuery, subjectFilter]);

  const handleAddTopic = (
    topic: { id: string; title: string; description: string },
    subtopics?: { id: string; title: string; durationMinutes: number }[],
  ) => {
    const newSubs: TopicSubtopic[] =
      subtopics && subtopics.length > 0
        ? subtopics.map((s) => ({
            id: s.id,
            title: s.title,
            durationMinutes: s.durationMinutes,
            selected: true,
          }))
        : [];

    const titleKey = topic.title.trim().toLowerCase();
    const existingTopic = selectedTopics.find(
      (t) => t.id === topic.id || t.title.trim().toLowerCase() === titleKey,
    );

    if (existingTopic) {
      setSelectedTopics((prev) =>
        prev.map((t) => {
          if (t.id !== existingTopic.id) return t;
          const existingIds = new Set((t.subtopics ?? []).map((s) => s.id));
          const merged = [
            ...(t.subtopics ?? []),
            ...newSubs.filter((s) => !existingIds.has(s.id)),
          ];
          const selected = merged.filter((s) => s.selected);
          const subSum = selected.reduce((sum, s) => sum + s.durationMinutes, 0);
          return {
            ...t,
            subtopics: merged.length > 0 ? merged : undefined,
            durationMinutes: selected.length > 0 ? (subSum > 0 ? subSum : null) : t.durationMinutes,
          };
        }),
      );
      return;
    }

    const subSum = newSubs.reduce((sum, s) => sum + s.durationMinutes, 0);
    const totalMin = newSubs.length > 0 ? (subSum > 0 ? subSum : null) : null;
    setSelectedTopics((prev) => [
      ...prev,
      {
        id: topic.id,
        title: topic.title,
        description: topic.description,
        durationMinutes: totalMin,
        category: 'fundamentals',
        workshopDayIndex: isWorkshop ? addDayTarget : undefined,
        subtopics: newSubs.length > 0 ? newSubs : undefined,
      },
    ]);
  };

  const handleRemoveTopic = (id: string) => {
    setSelectedTopics((prev) => prev.filter((t) => t.id !== id));
  };

  const handleDurationChange = (id: string, durationMinutes: number) => {
    setSelectedTopics((prev) =>
      prev.map((t) => (t.id === id ? { ...t, durationMinutes } : t)),
    );
  };

  const handleSubtopicDurationChange = (
    topicId: string,
    subId: string,
    minutes: number,
  ) => {
    setSelectedTopics((prev) =>
      prev.map((t) => {
        if (t.id !== topicId || !t.subtopics) return t;
        const nextSubs = t.subtopics.map((s) =>
          s.id === subId ? { ...s, durationMinutes: minutes } : s,
        );
        const selected = nextSubs.filter((s) => s.selected);
        return {
          ...t,
          subtopics: nextSubs,
          durationMinutes:
            selected.length > 0
              ? selected.reduce((sum, s) => sum + s.durationMinutes, 0)
              : t.durationMinutes,
        };
      }),
    );
  };

  const handleSubtopicRemove = (topicId: string, subId: string) => {
    setSelectedTopics((prev) =>
      prev.map((t) => {
        if (t.id !== topicId || !t.subtopics) return t;
        const nextSubs = t.subtopics.map((s) =>
          s.id === subId ? { ...s, selected: false } : s,
        );
        const selected = nextSubs.filter((s) => s.selected);
        return {
          ...t,
          subtopics: selected.length > 0 ? nextSubs : undefined,
          durationMinutes:
            selected.length > 0
              ? selected.reduce((sum, s) => sum + s.durationMinutes, 0) || null
              : t.durationMinutes,
        };
      }),
    );
  };

  const handleSubtopicTitleChange = (topicId: string, subId: string, title: string) => {
    setSelectedTopics((prev) =>
      prev.map((t) => {
        if (t.id !== topicId || !t.subtopics) return t;
        return {
          ...t,
          subtopics: t.subtopics.map((s) =>
            s.id === subId ? { ...s, title } : s,
          ),
        };
      }),
    );
  };

  const handleSubtopicReorder = (topicId: string, reorderedSubs: TopicSubtopic[]) => {
    setSelectedTopics((prev) =>
      prev.map((t) => {
        if (t.id !== topicId) return t;
        const selected = reorderedSubs.filter((s) => s.selected);
        const subSum = selected.reduce((sum, s) => sum + s.durationMinutes, 0);
        return {
          ...t,
          subtopics: reorderedSubs,
          durationMinutes: selected.length > 0 ? (subSum > 0 ? subSum : null) : t.durationMinutes,
        };
      }),
    );
  };

  const handleSubtopicAdd = (topicId: string) => {
    setSelectedTopics((prev) =>
      prev.map((t) => {
        if (t.id !== topicId) return t;
        const newSub = {
          id: `sub-${Date.now()}-${(t.subtopics?.length ?? 0)}`,
          title: '',
          durationMinutes: 0,
          selected: true,
        };
        const nextSubs = [...(t.subtopics ?? []), newSub];
        return { ...t, subtopics: nextSubs };
      }),
    );
  };

  const handleChangeTopicDay = (id: string, dayIndex: number) => {
    setSelectedTopics((prev) => {
      const next = prev.map((t) =>
        t.id === id ? { ...t, workshopDayIndex: dayIndex } : t,
      );
      const order = new Map(prev.map((t, i) => [t.id, i]));
      return [...next].sort((a, b) => {
        const dayDiff = (a.workshopDayIndex ?? 0) - (b.workshopDayIndex ?? 0);
        if (dayDiff !== 0) return dayDiff;
        return (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0);
      });
    });
  };

  const handleDayScheduleChange = (
    dayIndex: number,
    patch: { startDate?: string; startTime?: string },
  ) => {
    setDaySchedules((prev) => {
      const next = mergeDayTimings(
        workshopDayCount,
        createDraft?.startDate || '',
        createDraft?.startTime || '',
        prev,
      ).map((day) =>
        day.dayIndex === dayIndex
          ? {
              ...day,
              startDate: patch.startDate ?? day.startDate,
              startTime: patch.startTime ?? day.startTime,
            }
          : day,
      );
      if (createDraft) {
        saveCreateSessionDraft({
          ...createDraft,
          daySchedules: next,
          startDate: next[0]?.startDate || createDraft.startDate,
          startTime: next[0]?.startTime || createDraft.startTime,
        });
      }
      return next;
    });
  };

  const persistAndContinue = () => {
    if (!allTopicsHaveDuration(selectedTopics)) return;
    saveCurriculumPathForTimeline(selectedTopics);
    if (createDraft) {
      const nextDayTopics = isWorkshop
        ? groupTopicsByWorkshopDay(selectedTopics, workshopDayCount)
        : createDraft.dayTopics;
      saveCreateSessionDraft({
        ...createDraft,
        selectedTopics: selectedTopics.map((t) => t.title),
        dayTopics: nextDayTopics,
        daySchedules: isWorkshop ? daySchedules : createDraft.daySchedules,
        startDate:
          isWorkshop && daySchedules[0]?.startDate
            ? daySchedules[0].startDate
            : createDraft.startDate,
        startTime:
          isWorkshop && daySchedules[0]?.startTime
            ? daySchedules[0].startTime
            : createDraft.startTime,
      });
    }
    navigate(ROUTES.topicPlanner, { state: { fromCurriculum: true } });
  };

  const courseLabel = createDraft?.subjects?.[0] || 'this session';
  const sessionLabel = createDraft?.autoSessionName || 'Draft session';
  const structureLocked = Boolean(createDraft?.fromCoursePlan);
  const { width: pathWidth, onResizePointerDown } = useLearningPathResize();

  return (
    <AppLayout>
      <PageMain
        variant={structureLocked ? 'scroll' : 'fill'}
        className={`codelive-prepare-session${
          structureLocked ? ' codelive-prepare-session--locked' : ''
        }`}
      >
        <div className="codelive-prepare-session__chrome">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h6 className="mb-0">Curriculum Builder</h6>
              {!structureLocked && (
                <p className="text-muted small mb-0 mt-1">
                  Design the learning sequence for &apos;{courseLabel}&apos;
                </p>
              )}
            </div>
            <span className="badge text-bg-primary">{sessionLabel}</span>
          </div>

          {loadError && (
            <div className="alert alert-danger py-2 mb-0 mt-2" role="alert">
              {loadError}
            </div>
          )}
        </div>

        <div className="codelive-prepare-session__body">
          {!draftTrackId ? (
            <div className="alert alert-warning" role="alert">
              No session draft found. Go back to Step 1, select track/subject, then continue.
            </div>
          ) : (
            <div
              className={`codelive-prepare-session__split${
                structureLocked ? ' codelive-prepare-session__split--locked' : ''
              }`}
            >
              {!structureLocked && (
              <div className="codelive-prepare-session__library">
                <TopicLibrary
                  topics={filteredTopics}
                  filters={subjectFilters}
                  activeFilter={subjectFilter}
                  searchQuery={searchQuery}
                  selectedIds={selectedIds}
                  selectedSubIds={selectedSubIds}
                  onFilterChange={setSubjectFilter}
                  onSearchChange={setSearchQuery}
                  onAddTopic={handleAddTopic}
                  loading={loadingTopics}
                />
              </div>
              )}
              <div
                className={`codelive-prepare-session__path${
                  structureLocked ? ' codelive-prepare-session__path--locked' : ''
                }`}
                style={
                  structureLocked
                    ? undefined
                    : {
                        width: pathWidth,
                        flex: `0 0 ${pathWidth}px`,
                      }
                }
              >
                {!structureLocked && (
                <div
                  role="separator"
                  aria-orientation="vertical"
                  aria-label="Resize learning path"
                  title="Drag to resize (max 50% of screen)"
                  className="codelive-prepare-session__resize"
                  onPointerDown={onResizePointerDown}
                />
                )}
                {isWorkshop ? (
                  <WorkshopDayLearningPath
                    topics={selectedTopics}
                    dayCount={workshopDayCount}
                    daySchedules={daySchedules}
                    workshopStartDate={createDraft?.startDate || ''}
                    workshopStartTime={createDraft?.startTime || ''}
                    onRemove={handleRemoveTopic}
                    onReorder={setSelectedTopics}
                    onDurationChange={handleDurationChange}
                    onChangeTopicDay={handleChangeTopicDay}
                    onDayScheduleChange={handleDayScheduleChange}
                    onSubtopicDurationChange={handleSubtopicDurationChange}
                    onSubtopicRemove={handleSubtopicRemove}
                    onSubtopicTitleChange={handleSubtopicTitleChange}
                    onSubtopicReorder={handleSubtopicReorder}
                    addDayTarget={addDayTarget}
                    onAddDayTargetChange={setAddDayTarget}
                    courseId={createDraft?.courseId}
                    onContinue={persistAndContinue}
                    structureLocked={structureLocked}
                  />
                ) : (
                  <ActiveLearningPath
                    topics={selectedTopics}
                    onRemove={handleRemoveTopic}
                    onReorder={setSelectedTopics}
                    onDurationChange={handleDurationChange}
                    onSubtopicDurationChange={handleSubtopicDurationChange}
                    onSubtopicRemove={handleSubtopicRemove}
                    onSubtopicTitleChange={handleSubtopicTitleChange}
                    onSubtopicReorder={handleSubtopicReorder}
                    courseId={createDraft?.courseId}
                    onContinue={persistAndContinue}
                    structureLocked={structureLocked}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </PageMain>
    </AppLayout>
  );
}
